import * as React from 'react';
import {
    TextField,
    Select,
    Box,
    Typography,
    MenuItem,
    Button,
    Paper,
    Stack,
    FormHelperText
} from '@mui/material';
import { useToken } from '../items/UseToken';
import { useUser } from '../../Hooks/UserContext';

export default function AddApiKey({ handleClose, defaultVendor = '' }) {

    const [name, setName] = React.useState('');
    const [value, setValue] = React.useState('');
    const [vendor, setVendor] = React.useState(defaultVendor);
    const { user } = useUser();
    const registeredBy = user.username;


    const [errors, setErrors] = React.useState({});
    const { token } = useToken();

    const vendors = ['OpenAI', 'Google', 'Azure', 'Deepgram', 'ElevenLabs', 'Heygen'];

    const validateForm = () => {
        const newErrors = {};

        if (!name.trim()) newErrors.name = 'Key name is required.';
        if (!value.trim()) newErrors.value = 'Key value is required.';
        if (!vendor) newErrors.vendor = 'Vendor is required.';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        const payload = {
            name,
            value, // plaintext → backend zaszyfruje DPAPI
            vendor,
            registered_by: registeredBy || null
        };

        try {
            const response = await fetch('/api/key/CreateKey', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: 'Bearer ' + token,
                },
                body: JSON.stringify(payload),
            });

            // ❌ Backend zwrócił błąd walidacji
            if (!response.ok) {
                const errorText = await response.text();

                // 🔍 wykryj błąd duplikatu
                if (errorText.includes("already exists")) {
                    setErrors((prev) => ({
                        ...prev,
                        name: "This key name is already in use. Please choose another."
                    }));
                } else {
                    setErrors((prev) => ({
                        ...prev,
                        name: "Failed to create key. Try again."
                    }));
                }

                return; // 🚫 stop — nie zamykaj dialogu
            }

            // ✔ success
            if (handleClose)
            handleClose();

        } catch (err) {
            console.error(err);

            setErrors((prev) => ({
                ...prev,
                name: "Network error. Please try again."
            }));
        }
    };


    const isFormValid =
        name.trim() &&
        value.trim() &&
        vendor &&
        !errors.name &&
        !errors.value &&
        !errors.vendor;

    return (
        <Paper
            elevation={3}
            sx={{
                p: 4,
                maxWidth: 480,
                margin: '0 auto',
                borderRadius: 4,
                background: 'linear-gradient(145deg, #ffffff, #f9fafb)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.05)',
            }}
        >
            <Typography
                variant="h5"
                sx={{
                    mb: 3,
                    fontWeight: 600,
                    color: '#111827',
                    textAlign: 'center',
                    letterSpacing: 0.3,
                }}
            >
                Add API Key
            </Typography>

            <Stack spacing={3}>
                {/* NAME */}
                <TextField
                    label="Key Name"
                    variant="outlined"
                    fullWidth
                    value={name}
                    onChange={(e) => { setName(e.target.value); setErrors({}) }}
                    error={!!errors.name}
                    helperText={errors.name}
                />

                {/* VALUE */}
                <TextField
                    label="Key Value"
                    variant="outlined"
                    fullWidth
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    error={!!errors.value}
                    helperText={errors.value}
                />

                {/* VENDOR */}
                <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: '#374151' }}>
                        Vendor
                    </Typography>
                    <Select
                        value={vendor}
                        onChange={(e) => setVendor(e.target.value)}
                        displayEmpty
                        fullWidth
                        sx={{
                            borderRadius: 2,
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: errors.vendor ? '#ef4444' : '#d1d5db',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: errors.vendor ? '#ef4444' : '#6366f1',
                            },
                        }}
                    >
                        <MenuItem disabled value="">
                            <em>Select Vendor</em>
                        </MenuItem>
                        {vendors.map((v) => (
                            <MenuItem key={v} value={v}>
                                {v}
                            </MenuItem>
                        ))}
                    </Select>
                    {errors.vendor && <FormHelperText error>{errors.vendor}</FormHelperText>}
                </Box>


                {/* SUBMIT */}
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    size="large"
                    disabled={!isFormValid}
                    sx={{
                        mt: 2,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontSize: '1rem',
                        background: isFormValid
                            ? 'linear-gradient(135deg, #6366f1, #7c3aed)'
                            : 'linear-gradient(135deg, #cbd5e1, #e2e8f0)',
                        color: isFormValid ? 'white' : '#64748b',
                        boxShadow: isFormValid
                            ? '0 6px 18px rgba(99,102,241,0.25)'
                            : 'none',
                        cursor: isFormValid ? 'pointer' : 'not-allowed',
                        '&:hover': {
                            background: isFormValid
                                ? 'linear-gradient(135deg, #4f46e5, #6d28d9)'
                                : 'linear-gradient(135deg, #cbd5e1, #e2e8f0)',
                        },
                    }}
                >
                    Add API Key
                </Button>
            </Stack>
        </Paper>
    );
}
